import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import ForgotPasswordForm from './ForgotPasswordForm';
import { useAuth } from '../../hooks/useAuth';
import { useUniversity } from '../../contexts/UniversityContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(2px);
  display: ${props => props.$show ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1001;
  overflow-y: auto;
  padding: 20px;
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 480px) {
    padding: 10px;
    align-items: flex-start;
    padding-top: 40px;
  }
  
  @media (min-width: 768px) and (max-height: 800px) {
    align-items: flex-start;
    padding-top: 40px;
  }
`;

const ModalContainer = styled.div`
  background-color: ${props => props.theme.card};
  color: ${props => props.theme.text};
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  box-shadow: ${props => props.theme.shadow};
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 480px) {
    width: 100%;
    max-height: 90vh;
    margin: auto;
    border-radius: 10px;
  }
  
  @media (min-width: 768px) and (max-height: 800px) {
    max-width: 550px;
    max-height: 75vh;
  }
  
  @media (min-width: 1024px) and (max-height: 800px) {
    display: grid;
    grid-template-columns: 220px 1fr;
    grid-template-rows: auto 1fr;
    max-width: 700px;
    width: 70%;
    max-height: 600px;
    
    ${props => props.$activeTab === 'forgot' && `
      display: flex;
      width: 40%;
      max-width: 500px;
    `}
  }
`;

const ModalHeader = styled.div`
  padding: 15px;
  border-bottom: 1px solid ${props => props.theme.divider};
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 480px) {
    padding: 12px 10px;
  }
  
  @media (min-width: 1024px) and (max-height: 800px) {
    grid-column: 1 / 3;
    grid-row: 1;
  }
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: ${props => props.theme.primary};
  font-size: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  span {
    font-size: 26px;
  }
  
  @media (max-width: 480px) {
    font-size: 18px;
    gap: 6px;
    
    span {
      font-size: 20px;
    }
  }
`;

const ModalSubtitle = styled.p`
  color: ${props => props.theme.textSecondary};
  margin: 5px 0 0 0;
  font-size: 14px;
  
  @media (max-width: 480px) {
    font-size: 12px;
    margin: 3px 0 0 0;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: ${props => props.theme.textSecondary};
  
  &:hover {
    color: ${props => props.theme.text};
  }
  
  @media (max-width: 480px) {
    font-size: 20px;
  }
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid ${props => props.theme.divider};
  
  @media (min-width: 1024px) and (max-height: 800px) {
    flex-direction: column;
    grid-column: 1;
    grid-row: 2;
    border-bottom: none;
    border-right: 1px solid ${props => props.theme.divider};
  }
`;

const TabButton = styled.button`
  flex: 1;
  padding: 12px;
  background: ${props => props.$active ? props.theme.buttonLight : props.theme.card};
  border: none;
  font-weight: ${props => props.$active ? 'bold' : 'normal'};
  color: ${props => props.$active ? props.theme.primary : props.theme.buttonText};
  cursor: pointer;
  border-bottom: ${props => props.$active ? `2px solid ${props.theme.primary}` : `1px solid ${props.theme.divider}`};
  
  &:hover {
    background-color: ${props => props.$active ? props.theme.buttonLight : props.theme.buttonLightHover};
  }
  
  @media (max-width: 480px) {
    padding: 8px;
    font-size: 13px;
  }
  
  @media (min-width: 1024px) and (max-height: 800px) {
    padding: 20px 15px;
    border-bottom: none;
    border-left: ${props => props.$active ? `3px solid ${props.theme.primary}` : 'none'};
    text-align: left;
    font-size: 16px;
  }
`;

const FormsContainer = styled.div`
  padding: 20px;
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 480px) {
    padding: 12px 8px;
    max-height: calc(85vh - 100px);
  }
  
  @media (min-width: 768px) and (max-height: 800px) {
    padding: 15px;
    max-height: calc(75vh - 110px);
  }
  
  @media (min-width: 1024px) and (max-height: 800px) {
    grid-column: 2;
    grid-row: 2;
    padding: 25px;
    overflow-y: auto;
  }
`;

const AuthModal = ({ show, onClose, initialTab = 'login' }) => {
  const [activeTab, setActiveTab] = useState('login');
  const [isClosing, setIsClosing] = useState(false);
  const { user } = useAuth();
  const { changeUniversity } = useUniversity();
  
  useEffect(() => {
    if (user && user.university) {
      const currentUniversityId = localStorage.getItem('selectedUniversity');
      if (!currentUniversityId) {
        changeUniversity(user.university);
      }
    }
  }, [user, changeUniversity]);
  
  useEffect(() => {
    if (show) {
      setActiveTab(initialTab || 'login');
      setIsClosing(false);
      
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      if (!isClosing) {
        setTimeout(() => {
          setActiveTab('login');
        }, 300);
      }
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [show, initialTab, isClosing]);
  
  const handleClose = () => {
    if (!isClosing) {
      setIsClosing(true);
      onClose();
    }
  };
  
  const handleTabChange = (tab) => {
    if (!isClosing) {
      setActiveTab(tab);
    }
  };
  
  const handleOutsideClick = (e) => {
    if (e.target === e.currentTarget && !isClosing) {
      handleClose();
    }
  };
  
  if (!show) return null;
  
  return (
    <ModalOverlay $show={show} onClick={handleOutsideClick}>
      <ModalContainer $show={true} $activeTab={activeTab}>
        <ModalHeader>
          <div>
            <ModalTitle>
              {activeTab === 'login' ? (
                <>
                  <span>🤫</span> ¡Bienvenido de vuelta!
                </>
              ) : activeTab === 'register' ? (
                <>
                  <span>🎭</span> ¡Únete al chisme!
                </>
              ) : (
                <>
                  <span>🔑</span> Recupera tu acceso
                </>
              )}
            </ModalTitle>
            <ModalSubtitle>
              {activeTab === 'login' ? 
                "Los secretos de tu campus te esperan" :
                activeTab === 'register' ? 
                "Forma parte de la comunidad más controversial" :
                "Te enviaremos un enlace para recuperar tu cuenta"}
            </ModalSubtitle>
          </div>
          <CloseButton onClick={handleClose}>&times;</CloseButton>
        </ModalHeader>
        
        {activeTab !== 'forgot' && (
          <TabContainer>
            <TabButton 
              $active={activeTab === 'login'} 
              onClick={() => handleTabChange('login')}
              disabled={isClosing}
            >
              Iniciar sesión
            </TabButton>
            <TabButton 
              $active={activeTab === 'register'} 
              onClick={() => handleTabChange('register')}
              disabled={isClosing}
            >
              Registrarse
            </TabButton>
          </TabContainer>
        )}
        
        <FormsContainer>
          {activeTab === 'login' && (
            <LoginForm 
              onForgotPassword={() => handleTabChange('forgot')}
              onClose={handleClose}
            />
          )}
          
          {activeTab === 'register' && (
            <RegisterForm onClose={handleClose} />
          )}
          
          {activeTab === 'forgot' && (
            <ForgotPasswordForm 
              onBackToLogin={() => handleTabChange('login')} 
            />
          )}
        </FormsContainer>
        
        <ToastContainer
          position="top-center"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
          limit={3}
        />
      </ModalContainer>
    </ModalOverlay>
  );
};

export default AuthModal;